import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Menu as MenuIcon, ChevronDown, Check } from 'lucide-react';
import { useMobileNav } from '../../contexts/MobileNavContext';
import { useUser } from '../../contexts/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = { client: 'Client', provider: 'Provider', admin: 'Admin' };

// Role-based accent colors so it's instantly clear which role view you're in.
// Client = green, Provider = blue, Admin = orange (logo orange).
const ROLE_PILL = {
  client: 'bg-green-600',
  provider: 'bg-blue-600',
  admin: 'bg-orange-500',
};

// Quick-action button beside the role switcher — changes with the active role.
const ROLE_ACTIONS = {
  client: { label: 'Post a Job', to: '/create-job' },
  provider: { label: 'My Services', to: '/my-services' },
  admin: { label: 'Configure', to: '/owner?tab=config' },
};

/**
 * Clean, Airbnb-style mobile app header (native app only).
 * Logo + tappable role switcher + role-based quick action on the left,
 * notifications + menu on the right.
 */
export const MobileHeader = ({ user, unreadCount = 0 }) => {
  const navigate = useNavigate();
  const { setMenuOpen } = useMobileNav();
  const { switchRole } = useUser();
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const effectiveRole = user?.active_role || user?.role;
  const pillBg = ROLE_PILL[effectiveRole] || 'bg-gray-900';
  const availableRoles = (user?.available_roles && user.available_roles.length)
    ? user.available_roles
    : (user?.role ? [user.role] : []);
  const canSwitch = availableRoles.length > 1;

  // Context signals that make the quick-action smarter (provider w/ no services,
  // admin with missing critical config). Fetched once per role.
  const [signals, setSignals] = useState({ providerServiceCount: null, configIncomplete: false });
  useEffect(() => {
    if (!user || (effectiveRole !== 'provider' && effectiveRole !== 'admin')) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/badge-counts`);
        if (!cancelled) {
          setSignals({
            providerServiceCount: r.data.provider_service_count,
            configIncomplete: !!r.data.config_incomplete,
          });
        }
      } catch (e) {
        /* non-critical */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole, user?.id]);

  // Resolve the role-based quick action (context-aware).
  let roleAction = user ? ROLE_ACTIONS[effectiveRole] : null;
  let actionBadge = false;
  if (user) {
    if (effectiveRole === 'provider' && signals.providerServiceCount === 0) {
      roleAction = { label: 'Add Service', to: '/post-service' };
    } else if (effectiveRole === 'admin') {
      actionBadge = signals.configIncomplete;
    }
  }

  const handleSwitch = async (role) => {
    if (role === effectiveRole || switching) {
      setRoleMenuOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchRole(role);
      setRoleMenuOpen(false);
      navigate(role === 'admin' ? '/admin' : '/dashboard');
    } catch (e) {
      /* switchRole already logs the error */
    } finally {
      setSwitching(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="mobile-header"
    >
      <div className="flex items-center justify-between h-14 px-4">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="mobile-header-logo">
            <img src="/logo-dark.png" alt="GoodJob" className="h-8 w-8 object-contain shrink-0" />
            <span className="text-lg font-bold text-gray-900 tracking-tight truncate">GoodJob</span>
          </Link>

          {user && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => canSwitch && setRoleMenuOpen((o) => !o)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${pillBg} text-white capitalize whitespace-nowrap ${canSwitch ? 'active:scale-95 transition' : 'cursor-default'}`}
                data-testid="mobile-header-role"
                aria-label={canSwitch ? 'Switch role' : 'Current role'}
              >
                {ROLE_LABELS[effectiveRole] || effectiveRole}
                {canSwitch && <ChevronDown className="w-3 h-3" />}
              </button>

              {roleMenuOpen && canSwitch && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setRoleMenuOpen(false)}
                    data-testid="role-switcher-overlay"
                  />
                  <div
                    className="absolute left-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50"
                    data-testid="role-switcher-menu"
                  >
                    <p className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                      Switch role
                    </p>
                    {availableRoles.map((role) => (
                      <button
                        key={role}
                        onClick={() => handleSwitch(role)}
                        disabled={switching}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 capitalize"
                        data-testid={`role-switch-${role}`}
                      >
                        <span>{ROLE_LABELS[role] || role}</span>
                        {role === effectiveRole && <Check className="w-4 h-4 text-green-600" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {roleAction && (
            <button
              type="button"
              onClick={() => navigate(roleAction.to)}
              className={`relative shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold ${pillBg} text-white whitespace-nowrap active:scale-95 transition`}
              data-testid="header-role-action"
            >
              {roleAction.label}
              {actionBadge && (
                <span
                  className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white"
                  data-testid="header-role-action-badge"
                />
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {user && (
            <button
              onClick={() => navigate('/notifications')}
              className="relative p-2 rounded-full hover:bg-gray-100 active:scale-95 transition"
              data-testid="mobile-header-bell"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-gray-700" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
          )}

          <button
            onClick={() => setMenuOpen(true)}
            className="p-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition"
            data-testid="mobile-header-menu-btn"
            aria-label="Open menu"
          >
            {user?.avatar ? (
              <img
                src={user.avatar.startsWith('/api') ? `${BACKEND_URL}${user.avatar}` : user.avatar}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : user ? (
              <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold">
                {user.name?.charAt(0)?.toUpperCase()}
              </div>
            ) : (
              <MenuIcon className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
